import React, { useEffect, useState } from 'react'

import AdsClickIcon from '@mui/icons-material/AdsClick'
import { FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material'
import Button from '@mui/material/Button'
import { useSelector } from 'react-redux'

import type { SelectChangeEvent } from '@mui/material'
import type { Coordinate as OLCoordinate } from 'ol/coordinate'
import type { RootState } from 'reduxStore'

import * as Model from 'api/model'
import IsPointInBBox from 'map/IsPointInBBox'
import PoziZoom from 'map/zoom'
import { NumberField } from 'theme/components/NumberField'
import { DEFAULT_PROJECTION_CODE, WEB_MERCATOR_PROJECTION_CODE } from 'utils/constants'
import { runWhatsHere } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'
import type { PoziConfigBoundingBox } from 'utils/ProjUtils'
import { getRelevantProjections, getXCoordinateLabel, getYCoordinateLabel, proj4 } from 'utils/ProjUtils'

const CoordinateSelectTool: React.FC = () => {
  const [siteBoundingBox, setSiteBoundingBox] = useState<PoziConfigBoundingBox>([
    [0, 0],
    [0, 0]
  ])
  const [showControls, setShowControls] = useState(false)
  const [projection, setProjection] = useState<string>(DEFAULT_PROJECTION_CODE)
  const [projectionList, setProjectionList] = useState<React.ReactNode[]>([])
  const [xCoordinate, setXCoordinate] = useState<string>('')
  const [yCoordinate, setYCoordinate] = useState<string>('')
  const [xCoordinateLabel, setXCoordinateLabel] = useState<string>(getXCoordinateLabel(DEFAULT_PROJECTION_CODE))
  const [yCoordinateLabel, setYCoordinateLabel] = useState<string>(getYCoordinateLabel(DEFAULT_PROJECTION_CODE))

  // Get active tab ID from Redux state
  const activePanel = useSelector((state: RootState) => state.config?.layout?.drawer?.tabs?.activetabid ?? '') as string

  useEffect(() => {
    const siteBoundingBox = Model.get('config.layout.map.views.bbox', [
      [0, 0],
      [0, 0]
    ])
    setSiteBoundingBox(siteBoundingBox)

    // Get all projections relevant to the current site from ProjUtils, and populate the projection list
    const relevantProjections = getRelevantProjections(siteBoundingBox)
    const projectionList = []
    for (const [epsgId, projection] of Object.entries(relevantProjections)) {
      projectionList.push(
        <MenuItem key={epsgId} value={epsgId}>
          {projection.name} - {epsgId}
        </MenuItem>
      )
    }
    setProjectionList(projectionList)
  }, [])

  // Close controls when active tab changes away from "tools"
  useEffect(() => {
    if (activePanel !== 'tools') {
      setShowControls(false)
    }
  }, [activePanel])

  const handleToggleControls = () => {
    setShowControls(!showControls)
  }

  const handleProjectionChange = (e: SelectChangeEvent<string>) => {
    const selectedProjection = e.target.value
    setProjection(selectedProjection)
    setXCoordinateLabel(getXCoordinateLabel(selectedProjection))
    setYCoordinateLabel(getYCoordinateLabel(selectedProjection))
    // Clear coordinates on projection change
    setXCoordinate('')
    setYCoordinate('')
  }

  const handleCoordinateSelect = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }

    const x = parseFloat(xCoordinate)
    const y = parseFloat(yCoordinate)

    if (isNaN(x) || isNaN(y)) {
      console.error('Coordinates are not numbers:', { x, y })
      PoziNotifications().warning('Coordinates are not numbers.')
      return
    }

    // Reproject coordinate to default projection if necessary
    let defaultProjectionCoordinate: OLCoordinate = [x, y]
    if (projection !== DEFAULT_PROJECTION_CODE) {
      try {
        defaultProjectionCoordinate = proj4(projection, DEFAULT_PROJECTION_CODE, [x, y]) as OLCoordinate
      } catch (error) {
        console.error('Error reprojecting coordinates:', { x, y, projection })
        PoziNotifications().warning('Error reprojecting coordinates.')
        return
      }
    }

    const pointInSiteBbox = IsPointInBBox(defaultProjectionCoordinate, siteBoundingBox)
    if (!pointInSiteBbox) {
      console.error('Coordinates are outside the site boundary:', defaultProjectionCoordinate)
      PoziNotifications().warning('Coordinates are outside the site boundary.')
      return
    }

    // Zoom to the selected coordinate maintaining current zoom level
    PoziZoom().zoomToTarget(defaultProjectionCoordinate, true)

    // Transform to map projection for What's Here (EPSG:3857 - Pseudo Mercator)
    let mapProjectionCoordinate: OLCoordinate
    try {
      mapProjectionCoordinate = proj4(DEFAULT_PROJECTION_CODE, WEB_MERCATOR_PROJECTION_CODE, defaultProjectionCoordinate) as OLCoordinate
    } catch (error) {
      console.error('Error transforming coordinates to map projection:', { x, y, projection, error })
      PoziNotifications().warning('Error transforming coordinates to map projection.')
      return
    }

    // Run What's Here including any intersecting features
    runWhatsHere(mapProjectionCoordinate, true)
  }

  return (
    <Stack direction="column" spacing={2} sx={{ width: '280px' }}>
      <Button id="coordinateSelectTool" onClick={handleToggleControls} sx={{ width: '280px' }} variant="outlined">
        <AdsClickIcon />
        Select by coordinate
      </Button>
      {showControls && (
        <form onSubmit={handleCoordinateSelect} noValidate>
          <Stack direction="column" spacing={1} sx={{ paddingBottom: 2 }}>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
              <InputLabel id="coordinateSelectToolProjection-label">Projection</InputLabel>
              <Select labelId="coordinateSelectToolProjection-label" id="coordinateSelectToolProjection" value={projection} onChange={handleProjectionChange} label="Projection">
                {projectionList}
              </Select>
            </FormControl>
            <NumberField label={xCoordinateLabel} value={xCoordinate} onChange={(e) => setXCoordinate(e.target.value)} />
            <NumberField label={yCoordinateLabel} value={yCoordinate} onChange={(e) => setYCoordinate(e.target.value)} />
            <Button id="coordinateSelectToolGo" type="submit" disabled={!xCoordinate || !yCoordinate}>
              Select
            </Button>
          </Stack>
        </form>
      )}
    </Stack>
  )
}

export default CoordinateSelectTool
